import type { NumberSequenceFormValues } from "./number-sequence-schema";

export interface RegisteredModuleInfo {
  moduleCode: string;
  name: string;
  description: string;
  badge: string;
  defaultPrefix: string;
  defaultSuffix: string;
  defaultSize: number;
  defaultStep: number;
}

/**
 * Registry of modules that use auto numbering. To support a new module,
 * add one entry here and one in the API's `number_sequence.defaults.ts`.
 */
export const REGISTERED_MODULES: RegisteredModuleInfo[] = [
  {
    moduleCode: "sale",
    name: "Penjualan Toko / Kasir",
    description: "Transaksi kasir / POS di outlet",
    badge: "SALE",
    defaultPrefix: "SALE/%(Year)s%(Month)s/",
    defaultSuffix: "",
    defaultSize: 4,
    defaultStep: 1,
  },
  {
    moduleCode: "rider_sale",
    name: "Penjualan Keliling / Rider",
    description: "Transaksi penjualan canvasser / rider mobile",
    badge: "RDR",
    defaultPrefix: "RDR/%(Year)s%(Month)s/",
    defaultSuffix: "",
    defaultSize: 4,
    defaultStep: 1,
  },
  {
    moduleCode: "expense",
    name: "Pengeluaran Operasional (OE)",
    description: "Biaya dan pengeluaran operasional kas kecil",
    badge: "OE",
    defaultPrefix: "EXP/%(Year)s%(Month)s/",
    defaultSuffix: "",
    defaultSize: 4,
    defaultStep: 1,
  },
  {
    moduleCode: "rider_payroll",
    name: "Penggajian Rider (PYR)",
    description: "Slip gaji dan bonus rider",
    badge: "PYR",
    defaultPrefix: "PYR/%(Year)s%(Month)s/",
    defaultSuffix: "",
    defaultSize: 4,
    defaultStep: 1,
  },
  {
    moduleCode: "receivable",
    name: "Piutang Usaha (AR)",
    description: "Faktur penagihan dan piutang pelanggan",
    badge: "AR",
    defaultPrefix: "AR/%(Year)s%(Month)s/",
    defaultSuffix: "",
    defaultSize: 4,
    defaultStep: 1,
  },
  {
    moduleCode: "stock_opname_raw_material",
    name: "Stock Opname Bahan Baku (OPN-RM)",
    description: "Pemeriksaan fisik stok bahan baku",
    badge: "OPN-RM",
    defaultPrefix: "OPN-RM/%(Year)s%(Month)s/",
    defaultSuffix: "",
    defaultSize: 4,
    defaultStep: 1,
  },
  {
    moduleCode: "stock_opname_product",
    name: "Stock Opname Produk (OPN-PRD)",
    description: "Pemeriksaan fisik stok produk jadi",
    badge: "OPN-PRD",
    defaultPrefix: "OPN-PRD/%(Year)s%(Month)s/",
    defaultSuffix: "",
    defaultSize: 4,
    defaultStep: 1,
  },
  {
    moduleCode: "stock_adjustment",
    name: "Penyesuaian Stok (ADJ)",
    description: "Koreksi stok masuk / keluar dengan alasan",
    badge: "ADJ",
    defaultPrefix: "ADJ/%(Year)s%(Month)s/",
    defaultSuffix: "",
    defaultSize: 4,
    defaultStep: 1,
  },
  {
    moduleCode: "stock_repack",
    name: "Repacking Bahan Baku (RPK)",
    description: "Pecah atau gabung kemasan bahan baku",
    badge: "RPK",
    defaultPrefix: "RPK/%(Year)s%(Month)s/",
    defaultSuffix: "",
    defaultSize: 4,
    defaultStep: 1,
  },
  {
    moduleCode: "stock_production",
    name: "Produksi Barang Jadi (PRO)",
    description: "Produksi produk jadi dari resep",
    badge: "PRO",
    defaultPrefix: "PRO/%(Year)s%(Month)s/",
    defaultSuffix: "",
    defaultSize: 4,
    defaultStep: 1,
  },
  {
    moduleCode: "opening_stock",
    name: "Stok Awal (STA)",
    description: "Pencatatan saldo stok awal toko dan HPP awal",
    badge: "STA",
    defaultPrefix: "STA/%(Year)s%(Month)s/",
    defaultSuffix: "",
    defaultSize: 4,
    defaultStep: 1,
  },
  {
    moduleCode: "product",
    name: "Katalog Produk (SKU)",
    description: "Nomor SKU otomatis untuk item katalog produk",
    badge: "PRD",
    defaultPrefix: "PRD/%(Year)s/",
    defaultSuffix: "",
    defaultSize: 4,
    defaultStep: 1,
  },
  {
    moduleCode: "raw_material",
    name: "Bahan Baku (SKU)",
    description: "Nomor SKU otomatis untuk item bahan baku",
    badge: "RM",
    defaultPrefix: "RM/%(Year)s/",
    defaultSuffix: "",
    defaultSize: 4,
    defaultStep: 1,
  },
];

export const SEQUENCE_PLACEHOLDERS = [
  {
    tag: "%(ExtBranchId)s",
    label: "Kode Cabang / Outlet",
    sample: "CAB-01",
    description: "Kode unit cabang atau workspace",
  },
  {
    tag: "%(Year)s",
    label: "Tahun 4-Digit",
    sample: "2026",
    description: "Format 4 digit tahun (YYYY)",
  },
  {
    tag: "%(Y)s",
    label: "Tahun 2-Digit",
    sample: "26",
    description: "Format 2 digit tahun tanpa abad (YY)",
  },
  {
    tag: "%(Month)s",
    label: "Bulan 2-Digit",
    sample: "10",
    description: "Format 2 digit bulan (01-12)",
  },
  {
    tag: "%(Day)s",
    label: "Tanggal 2-Digit",
    sample: "08",
    description: "Format 2 digit tanggal hari (01-31)",
  },
] as const;

export const SEQUENCE_FORMAT_PRESETS = [
  {
    label: "Standar (Tahun + Bulan)",
    prefix: "SALE/%(Year)s%(Month)s/",
    suffix: "",
  },
  {
    label: "Cabang + Tahun + Bulan",
    prefix: "INV/%(ExtBranchId)s/%(Year)s/%(Month)s/",
    suffix: "",
  },
  {
    label: "Harian Lengkap",
    prefix: "TRX/%(ExtBranchId)s/%(Year)s%(Month)s%(Day)s/",
    suffix: "",
  },
  {
    label: "Dengan Suffix Cabang",
    prefix: "DOC/%(Year)s/",
    suffix: "/%(ExtBranchId)s",
  },
];

export function getDefaultConfigForModule(
  moduleCode: string,
): NumberSequenceFormValues {
  const mod = REGISTERED_MODULES.find((m) => m.moduleCode === moduleCode);
  return {
    moduleCode,
    name: mod?.name ?? moduleCode.toUpperCase(),
    prefix:
      mod?.defaultPrefix ?? `${moduleCode.toUpperCase()}/%(Year)s%(Month)s/`,
    suffix: mod?.defaultSuffix ?? "",
    size: mod?.defaultSize ?? 4,
    step: mod?.defaultStep ?? 1,
    next: 1,
    resetPeriod: "monthly",
    autoGenerateOnCreate: true,
  };
}

export function generateSequencePreview(params: {
  prefix: string;
  suffix?: string;
  size: number;
  next: number;
  branchCode?: string;
  date?: Date;
}): string {
  const date = params.date ?? new Date();
  const branchCode = params.branchCode || "CAB-01";

  const evaluate = (tmpl?: string): string => {
    if (!tmpl) return "";
    return tmpl.replace(/%\(([^)]+)\)(s)?/g, (_, key: string) => {
      switch (key) {
        case "Year":
          return String(date.getFullYear());
        case "Y":
          return String(date.getFullYear()).slice(-2);
        case "Month":
          return String(date.getMonth() + 1).padStart(2, "0");
        case "Day":
          return String(date.getDate()).padStart(2, "0");
        case "ExtBranchId":
          return branchCode;
        default:
          return `(${key})`;
      }
    });
  };

  const paddedNext = String(params.next).padStart(
    Math.max(1, params.size || 4),
    "0",
  );

  return `${evaluate(params.prefix)}${paddedNext}${evaluate(params.suffix)}`;
}
